import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import API from '../api/axios';
import { formatRole } from '../utils/roleLabel';
import './SMEStyles.css';

const FileIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
  </svg>
);
const UserCheckIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><polyline points="16 11 18 13 22 9" />
  </svg>
);
const UsersIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
  </svg>
);
const ClipboardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
  </svg>
);
const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const ModeratorDashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [college, setCollege] = useState(null);
  const [stats, setStats] = useState({
    pendingPapers: 0,
    smes: 0,
    examiners: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [pendingRes, smeRes, examinerRes, collegeRes] = await Promise.all([
        API.get('/moderator-categorization/pending-requests').catch(() => ({ data: { requests: [] } })),
        API.get('/users?role=subject_matter_expert&limit=1').catch(() => ({ data: { total: 0 } })),
        API.get('/users?role=examiner&limit=1').catch(() => ({ data: { total: 0 } })),
        API.get('/colleges/my-college').catch(() => ({ data: { college: null } })),
      ]);

      const requests = pendingRes.data.requests || [];
      const pendingPapers = requests.filter(r => r.status === 'pending_moderator').length;

      setCollege(collegeRes.data.college);
      setStats({
        pendingPapers,
        smes: smeRes.data.total || 0,
        examiners: examinerRes.data.total || 0
      });
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="sme-page">
        <div className="sme-spinner-wrap">
          <div className="sme-spinner" />
        </div>
      </div>
    );
  }

  const statCards = [
    { icon: <FileIcon />, colorClass: 'sme-stat-icon--blue', value: stats.pendingPapers, label: 'Pending Papers', onClick: () => navigate('/moderator-categorization') },
    { icon: <UserCheckIcon />, colorClass: 'sme-stat-icon--green', value: stats.smes, label: 'Subject Matter Experts', onClick: () => navigate('/moderator/users') },
    { icon: <UsersIcon />, colorClass: 'sme-stat-icon--purple', value: stats.examiners, label: 'Examiners', onClick: () => navigate('/moderator/users') },
  ];

  const quickActions = [
    { icon: <ClipboardIcon />, colorClass: 'sme-stat-icon--blue', label: 'Categorize Papers', desc: 'Categorize approved paper sets into exam types', path: '/moderator-categorization' },
    { icon: <CheckCircleIcon />, colorClass: 'sme-stat-icon--green', label: 'Approved Requests', desc: 'View completed and categorized paper sets', path: '/moderator/approvals' },
    { icon: <UsersIcon />, colorClass: 'sme-stat-icon--purple', label: 'Manage Users', desc: 'Manage SMEs and examiners in your subject', path: '/moderator/users' },
    // { icon: <LayersIcon />, colorClass: 'sme-stat-icon--amber', label: 'University Pool', desc: 'Generate sets from pool selections', path: '/university-pool/moderator' },
  ];

  return (
    <div className="sme-page">
      <div className="sme-page-header">
        <div>
          <h1>Moderator Dashboard</h1>
          <p>Welcome back, {user?.name} ({formatRole(user?.role)})</p>
          <p>{college?.college_name || 'Categorize papers and manage your subject faculty'}</p>
        </div>
      </div>

      <div className="sme-stats-grid">
        {statCards.map(card => (
          <div
            key={card.label}
            className="sme-stat-card sme-stat-card--clickable"
            onClick={card.onClick}
          >
            <div className={`sme-stat-icon ${card.colorClass}`}>{card.icon}</div>
            <div>
              <div className="sme-stat-value">{card.value}</div>
              <div className="sme-stat-label">{card.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="sme-card">
        <div className="sme-card-header">
          <h2>Quick Actions</h2>
        </div>
        <div className="sme-card-body">
          <div className="sme-actions-grid">
            {quickActions.map(action => (
              <div
                key={action.path}
                className="sme-action-card"
                onClick={() => navigate(action.path)}
              >
                <div className={`sme-action-icon ${action.colorClass}`}>{action.icon}</div>
                <div>
                  <div className="sme-action-title">{action.label}</div>
                  <div className="sme-action-desc">{action.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModeratorDashboard;
